import { loadScript, waitForCondition } from '@ocx/utils';

import './devrev-plug-sdk.augment';
import { DevRevPlugSDKIdentityParams, DevRevPlugSDKInitParams } from './devrev-plug-sdk.types';
import { DEVREV_PLUG_SDK_URL } from './devrev-plug-sdk.constants';

export class DevRevPlugSDK {
  private static isScriptLoaded = false;
  /**
   * There is a !!globalThis.plugSDK?.isPlugInitialized
   * But its behaviour is unpredicted sometimes.
   * @private
   */
  private static isReady = false;

  private static async init(params: DevRevPlugSDKInitParams): Promise<void> {
    if (this.isReady) {
      return;
    }

    if (!this.isScriptLoaded) {
      await loadScript(DEVREV_PLUG_SDK_URL);
      this.isScriptLoaded = true;
    }

    globalThis.plugSDK.init(params);

    globalThis.plugSDK.onEvent((payload) => {
      if (payload.type === 'ON_PLUG_WIDGET_READY') {
        this.isReady = true;
      }
      if (payload.type === 'ON_PLUG_WIDGET_OPENED') {
        const widgetElement = document.querySelector<HTMLElement>('[data-drid="PLUG_WIDGET_IFRAME"]');
        if (!widgetElement) {
          return;
        }
        widgetElement.style.paddingTop = 'var(--ion-safe-area-top)';
        widgetElement.style.paddingBottom = 'var(--ion-safe-area-bottom)';
      }
      if (payload.type === 'ON_PLUG_WIDGET_CLOSED') {
        // This is a fast way to make sure we initialize the widget from scratch next time.
        this.shutdown();
      }
    });
  }

  public static shutdown() {
    if (globalThis.plugSDK?.isPlugInitialized) {
      globalThis.plugSDK.shutdown();
      this.isReady = false;
    }
  }

  /**
   * Opens the Plug widget, initializing the SDK if necessary.
   * @param params Initialization and identification parameters.
   */
  public static async open(params: DevRevPlugSDKInitParams): Promise<void> {
    await this.init(params);
    await this.waitIsReady();
    globalThis.plugSDK.toggleWidget(true);
    await this.waitOpened();
  }

  /**
   * Note that the updateIdentity method cannot be used to update the user_ref of the user.
   * In order to change the identity of the user completely to a new one, you need to re-initialize Plug.
   * See the https://developer.devrev.ai/sdks/web/user-identity#changing-the-user-identity.
   * @param params Identification parameters.
   */
  private static async updateIdentity(params: DevRevPlugSDKIdentityParams) {
    await this.waitIsReady();
    globalThis.plugSDK.updateIdentity(params);
  }

  private static async waitOpened(): Promise<boolean> {
    try {
      await waitForCondition(() => globalThis.plugSDK?.isWidgetOpen, {
        initialDelay: 300,
        strategy: 'linear',
      });
      return true;
    } catch {
      return false;
    }
  }

  private static async waitIsReady(): Promise<boolean> {
    try {
      await waitForCondition(() => this.isReady);
      return true;
    } catch {
      return false;
    }
  }
}
